import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CriteriosBuilder, emptyCrit, normalizeCrit, serializeCrits } from "@/components/CriteriosBuilder";
import { ESCALA_PRATICA } from "@/lib/constants";
import { useUnsavedGuard } from "@/hooks/useUnsavedGuard";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

const empty = (cursoId) => ({ nome: "", objetivo: "", curso_id: cursoId || "", modulo: "", tipologia_cec_id: "", historico_contexto: "", dinamica: "", usa_escala: false });

const NOME_SUGESTOES = [
  "Oficina 01 - Negociação Preliminar",
  "Oficina 02 - Contato Inicial, Estabilização",
  "Oficina 03 - Contenção e Controle da Comunicação",
  "Oficina 04 - Escuta Ativa e Redução Emocional",
];
const MODULO_SUGESTOES = [
  "Módulo 01 - Avaliação Prática Integrada",
  "Módulo 02 - Primeira Intervenção e Negociação Preliminar",
  "Módulo 03 - Contenção e Isolamento",
  "Módulo 04 - Intervenção Inicial",
];
const OBJETIVO_SUGESTOES = [
  "Avaliar o contato inicial, a comunicação e a estabilização da crise.",
];
const DINAMICA_SUGESTOES = [
  "O aluno realiza o primeiro contato e busca estabilizar o CEC por meio da comunicação.",
];

export function OficinaFormDialog({ open, onOpenChange, oficina, onSaved, lockCursoId }) {
  const [cursos, setCursos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [banco, setBanco] = useState([]);
  const [bancoCriterios, setBancoCriterios] = useState([]);
  const [tipologias, setTipologias] = useState([]);
  const [form, setForm] = useState(empty());
  const [criterios, setCriterios] = useState([emptyCrit()]);
  const [modeloSel, setModeloSel] = useState("");
  const { setBaseline, guard } = useUnsavedGuard();

  useEffect(() => {
    if (!open) return;
    api.get("/cursos").then((r) => setCursos(r.data));
    api.get("/tipologias-cec").then((r) => setTipologias(r.data));
    api.get("/modelos-ficha").then((r) => setModelos(r.data));
    api.get("/indicadores").then((r) => setBanco(r.data));
    setModeloSel("");
    let f, crits;
    if (oficina) {
      f = { nome: oficina.nome || "", objetivo: oficina.objetivo || "", curso_id: oficina.curso_id || "", modulo: oficina.modulo || "", tipologia_cec_id: oficina.tipologia_cec_id || "", historico_contexto: oficina.historico_contexto || "", dinamica: oficina.dinamica || "", usa_escala: !!oficina.escala_labels };
      crits = (oficina.criterios || []).length ? oficina.criterios.map(normalizeCrit) : [emptyCrit()];
    } else {
      f = empty(lockCursoId);
      crits = [emptyCrit()];
    }
    setForm(f); setCriterios(crits); setBaseline({ form: f, criterios: crits });
  }, [open, oficina, lockCursoId]);

  const selectedCurso = cursos.find((c) => c.id === form.curso_id);
  const requiresTip = !!selectedCurso?.usa_tipologia_cec;
  const modelosDisponiveis = modelos.filter((m) => !m.curso_id || m.curso_id === form.curso_id);

  const aplicarModelo = (mid) => {
    setModeloSel(mid);
    const m = modelos.find((x) => x.id === mid);
    if (m) {
      setCriterios((m.criterios || []).length ? m.criterios.map(normalizeCrit) : [emptyCrit()]);
      if (m.escala_labels) setForm((f) => ({ ...f, usa_escala: true }));
      toast.success(`Critérios do modelo "${m.nome}" copiados. Você pode ajustá-los.`);
    }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        tipologia_cec_id: form.tipologia_cec_id || "",
        modelo_ficha_id: modeloSel || "",
        criterios: serializeCrits(criterios),
        escala_labels: form.usa_escala ? ESCALA_PRATICA : null,
        media_minima: form.usa_escala ? 3.0 : null,
      };
      delete payload.usa_escala;
      if (oficina) { await api.put(`/oficinas/${oficina.id}`, payload); toast.success("Oficina atualizada"); }
      else { await api.post("/oficinas", payload); toast.success("Oficina criada"); }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!open || !form.curso_id) { setBancoCriterios([]); return; }
    api.get(`/banco-criterios?curso_id=${form.curso_id}`).then((r) => setBancoCriterios(r.data)).catch(() => setBancoCriterios([]));
  }, [open, form.curso_id]);

  const cursoLocked = !!oficina || !!lockCursoId;

  // Indicadores do CONTEXTO (derivados do banco de critérios da formação atual),
  // evitando misturar conteúdo de Negociação numa instrução de Primeiro Interventor.
  const indicadoresBanco = bancoCriterios.length
    ? Array.from(new Map(
        bancoCriterios.flatMap((c) => (c.indicadores || []).map((ind) => [(ind.nome || "").toLowerCase(), { id: ind.nome, nome: ind.nome, descricao: ind.descricao || "" }]))
      ).values()).filter((x) => x.nome)
    : banco;

  return (
    <Dialog open={open} onOpenChange={guard(onOpenChange, { form, criterios })}>
      <DialogContent className="bg-popover border-border max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{oficina ? "Editar oficina" : "Nova oficina"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input data-testid="oficina-nome" list="oficina-nome-list" autoComplete="off" placeholder="Selecione uma sugestão ou digite" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              <datalist id="oficina-nome-list">{NOME_SUGESTOES.map((o) => <option key={o} value={o} />)}</datalist>
            </div>
            <div>
              <Label>Módulo</Label>
              <Input data-testid="oficina-modulo" list="oficina-modulo-list" autoComplete="off" placeholder="Selecione uma sugestão ou digite" value={form.modulo} onChange={(e) => setForm({ ...form, modulo: e.target.value })} />
              <datalist id="oficina-modulo-list">{MODULO_SUGESTOES.map((o) => <option key={o} value={o} />)}</datalist>
            </div>
          </div>
          <div>
            <Label>Curso</Label>
            <Select value={form.curso_id} onValueChange={(v) => setForm({ ...form, curso_id: v })} disabled={cursoLocked}>
              <SelectTrigger data-testid="oficina-curso"><SelectValue placeholder="Selecione o curso" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {cursos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipologia do CEC {requiresTip ? <span className="text-red-400">*</span> : <span className="text-muted-foreground text-xs font-normal">(opcional)</span>}</Label>
            <Select value={form.tipologia_cec_id || "none"} onValueChange={(v) => setForm({ ...form, tipologia_cec_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="oficina-tipologia"><SelectValue placeholder="Selecione a tipologia" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="none">Sem tipologia</SelectItem>
                {tipologias.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Objetivo</Label>
              <Select value="" onValueChange={(v) => setForm({ ...form, objetivo: v })}>
                <SelectTrigger data-testid="oficina-objetivo-sugestao" className="h-7 w-auto text-xs gap-1"><SelectValue placeholder="Sugestões" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-w-[90vw]">
                  {OBJETIVO_SUGESTOES.map((o) => <SelectItem key={o} value={o} className="text-xs whitespace-normal">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea data-testid="oficina-objetivo" value={form.objetivo} onChange={(e) => setForm({ ...form, objetivo: e.target.value })} placeholder="Selecione uma sugestão ou digite livremente" />
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>Escala de Negociação (1-5)</Label>
                <p className="text-[11px] text-muted-foreground">Insuficiente · Regular · Bom · Muito Bom · Ótimo · aprovação com média ≥ 3,00 (APTO)</p>
              </div>
              <Switch data-testid="oficina-usa-escala" checked={form.usa_escala} onCheckedChange={(v) => setForm({ ...form, usa_escala: v })} />
            </div>
            <div><Label>Contexto do Cenário</Label><Textarea data-testid="oficina-historico" value={form.historico_contexto} onChange={(e) => setForm({ ...form, historico_contexto: e.target.value })} placeholder="Contexto da ocorrência simulada..." /></div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label>Dinâmica</Label>
                <Select value="" onValueChange={(v) => setForm({ ...form, dinamica: v })}>
                  <SelectTrigger data-testid="oficina-dinamica-sugestao" className="h-7 w-auto text-xs gap-1"><SelectValue placeholder="Sugestões" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border max-w-[90vw]">
                    {DINAMICA_SUGESTOES.map((o) => <SelectItem key={o} value={o} className="text-xs whitespace-normal">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea data-testid="oficina-dinamica" value={form.dinamica} onChange={(e) => setForm({ ...form, dinamica: e.target.value })} placeholder="Selecione uma sugestão ou digite como a atividade se desenrola..." />
            </div>
          </div>

          {!!modelosDisponiveis.length && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              <Label className="flex items-center gap-1.5"><FileDown className="h-4 w-4 text-primary" />Aplicar Modelo de Ficha</Label>
              <p className="text-[11px] text-muted-foreground mb-2">Copia os critérios do modelo para esta oficina (você pode ajustá-los depois).</p>
              <Select value={modeloSel} onValueChange={aplicarModelo}>
                <SelectTrigger data-testid="oficina-modelo"><SelectValue placeholder="Selecione um modelo" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {modelosDisponiveis.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome} ({(m.criterios || []).length})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <CriteriosBuilder criterios={criterios} setCriterios={setCriterios} banco={indicadoresBanco} bancoCriterios={bancoCriterios} />
        </div>
        <DialogFooter>
          <Button data-testid="salvar-oficina-btn" onClick={save} disabled={!form.nome || !form.curso_id || (requiresTip && !form.tipologia_cec_id)}>{oficina ? "Salvar alterações" : "Salvar oficina"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
